// Charts: the interactive price chart (TradingView Lightweight Charts) and small SVG charts
// (bars for financials and dividends, a sparkline for the overview). Colours come from CSS tokens.
import {
  CandlestickSeries,
  ColorType,
  createChart,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type UTCTimestamp,
} from "lightweight-charts";
import { esc } from "./format";

export interface Bar { t: number; o: number; h: number; l: number; c: number; v: number }

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function sma(bars: Bar[], period: number) {
  const out: { time: UTCTimestamp; value: number }[] = [];
  let sum = 0;
  bars.forEach((b, i) => {
    sum += b.c;
    if (i >= period) sum -= bars[i - period].c;
    if (i >= period - 1) out.push({ time: b.t as UTCTimestamp, value: sum / period });
  });
  return out;
}

export interface PriceChartOptions {
  style: "candles" | "line";
  sma50: boolean;
  sma200: boolean;
  from: number; // unix seconds: first bar shown
}

// Draws into el, replacing anything there. Returns the chart so the caller can remove it.
export function priceChart(el: HTMLElement, bars: Bar[], o: PriceChartOptions): IChartApi {
  el.replaceChildren();
  const up = css("--up"), down = css("--down"), text = css("--muted"), grid = css("--line");
  const chart = createChart(el, {
    autoSize: true,
    layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: text, attributionLogo: false },
    grid: { vertLines: { visible: false }, horzLines: { color: grid } },
    rightPriceScale: { borderVisible: false },
    timeScale: { borderVisible: false },
    handleScroll: { vertTouchDrag: false },
  });
  if (o.style === "candles") {
    chart.addSeries(CandlestickSeries, {
      upColor: up, downColor: down, wickUpColor: up, wickDownColor: down, borderVisible: false,
    }).setData(bars.map((b) => ({ time: b.t as UTCTimestamp, open: b.o, high: b.h, low: b.l, close: b.c })));
  } else {
    chart.addSeries(LineSeries, { color: css("--accent"), lineWidth: 2 })
      .setData(bars.map((b) => ({ time: b.t as UTCTimestamp, value: b.c })));
  }
  if (o.sma50) chart.addSeries(LineSeries, { color: css("--series-2"), lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(sma(bars, 50));
  if (o.sma200) chart.addSeries(LineSeries, { color: css("--series-3"), lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(sma(bars, 200));

  const vol = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false });
  vol.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
  vol.setData(bars.map((b, i) => ({
    time: b.t as UTCTimestamp,
    value: b.v,
    color: (i && b.c < bars[i - 1].c ? down : up) + "55",
  })));

  const last = bars.at(-1)?.t;
  if (last) chart.timeScale().setVisibleRange({ from: Math.max(o.from, bars[0].t) as UTCTimestamp, to: last as UTCTimestamp });
  return chart;
}

// Thin line of closing prices, scaled to fit.
export function sparkline(values: number[], w = 320, h = 64): string {
  if (values.length < 2) return "";
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const pts = values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 4 - ((v - min) / span) * (h - 8)).toFixed(1)}`);
  const cls = values.at(-1)! >= values[0] ? "up" : "down";
  return `<svg class="spark ${cls}" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true">
    <polyline points="${pts.join(" ")}" fill="none" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/></svg>`;
}

export interface BarItem { label: string; value: number }

// Vertical bars with value labels; negative values hang below a zero line. Re-renders on resize.
export function barChart(el: HTMLElement, items: BarItem[], format: (v: number) => string, series = 1) {
  const draw = () => {
    const max = Math.max(0, ...items.map((i) => i.value)), min = Math.min(0, ...items.map((i) => i.value));
    // Room under the bars for x labels, plus the value labels of negative bars.
    const w = Math.max(el.clientWidth, 240), top = 20, bottom = min < 0 ? 40 : 24, h = 170 + bottom;
    const span = max - min || 1;
    const y = (v: number) => top + ((max - v) / span) * (h - top - bottom);
    const slot = w / items.length, bw = Math.min(56, slot * 0.62);
    const bars = items.map((it, i) => {
      const x = i * slot + (slot - bw) / 2, y0 = y(0), y1 = y(it.value);
      const neg = it.value < 0;
      const label = format(it.value);
      return `<g><title>${esc(it.label)}: ${esc(label)}</title>
        <rect x="${x.toFixed(1)}" y="${Math.min(y0, y1).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, Math.abs(y1 - y0)).toFixed(1)}" rx="3" class="${neg ? "neg" : `s${series}`}"/>
        <text x="${(x + bw / 2).toFixed(1)}" y="${(neg ? y1 + 13 : y1 - 5).toFixed(1)}" class="val">${esc(label)}</text>
        <text x="${(x + bw / 2).toFixed(1)}" y="${h - 6}" class="lbl">${esc(it.label)}</text></g>`;
    });
    el.innerHTML = `<svg class="bars" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img">
      <line x1="0" x2="${w}" y1="${y(0).toFixed(1)}" y2="${y(0).toFixed(1)}" class="zero"/>${bars.join("")}</svg>`;
  };
  draw();
  let lastWidth = el.clientWidth;
  new ResizeObserver(() => {
    if (Math.abs(el.clientWidth - lastWidth) > 4) { lastWidth = el.clientWidth; draw(); }
  }).observe(el);
}
